import { useCallback, useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import {
  ExternalStorageActions,
  kExternalStorageRequestStatus,
  selectActiveMatterExternalSyncMapping,
  selectExternalStorageAccountsRequest,
  selectMatterExternalSyncMapping,
  selectMatterExternalSyncMappingRequest,
  selectVisibleConnectedExternalAccount,
} from '@/user_files/external-storage/store/slice'

type UseMatterExternalSyncStatusParams = {
  matterId: string | null | undefined
  enabled: boolean
}

export function useMatterExternalSyncStatus({ matterId, enabled }: UseMatterExternalSyncStatusParams) {
  const dispatch = useAppDispatch()
  const connectedAccount = useAppSelector(selectVisibleConnectedExternalAccount)
  const accountsRequest = useAppSelector(selectExternalStorageAccountsRequest)
  const mapping = useAppSelector((state) => selectMatterExternalSyncMapping(state, matterId))
  const mappingRequest = useAppSelector((state) => selectMatterExternalSyncMappingRequest(state, matterId))
  const activeMapping = useAppSelector((state) => selectActiveMatterExternalSyncMapping(state, matterId))

  const reload = useCallback(() => {
    dispatch(ExternalStorageActions.fetchConnectedAccounts())
    if (matterId) {
      dispatch(ExternalStorageActions.fetchMatterExternalSyncMapping(matterId))
    }
  }, [dispatch, matterId])

  useEffect(() => {
    if (!enabled) return

    if (accountsRequest.status === kExternalStorageRequestStatus.IDLE) {
      dispatch(ExternalStorageActions.fetchConnectedAccounts())
    }
  }, [accountsRequest.status, dispatch, enabled])

  useEffect(() => {
    if (!enabled || !matterId) return

    if (mappingRequest.status === kExternalStorageRequestStatus.IDLE) {
      dispatch(ExternalStorageActions.fetchMatterExternalSyncMapping(matterId))
    }
  }, [dispatch, enabled, mappingRequest.status, matterId])

  const connectedProvider = connectedAccount?.provider ?? null
  const syncedProvider = activeMapping?.provider ?? null
  const displayProvider = syncedProvider ?? connectedProvider
  const loading =
    enabled &&
    ((!!matterId && (mappingRequest.status === kExternalStorageRequestStatus.IDLE || mappingRequest.status === kExternalStorageRequestStatus.LOADING)) ||
      (activeMapping == null &&
        (accountsRequest.status === kExternalStorageRequestStatus.IDLE || accountsRequest.status === kExternalStorageRequestStatus.LOADING)))
  const error =
    enabled &&
    (mappingRequest.status === kExternalStorageRequestStatus.ERROR || (activeMapping == null && accountsRequest.status === kExternalStorageRequestStatus.ERROR))

  return useMemo(
    () => ({
      connectedAccount,
      connectedProvider,
      syncedProvider,
      displayProvider,
      mapping,
      activeMapping,
      hasConnectedAccount: connectedAccount != null,
      hasActiveMatterSync: activeMapping != null,
      loading,
      error,
      reload,
    }),
    [activeMapping, connectedAccount, connectedProvider, displayProvider, error, loading, mapping, reload, syncedProvider]
  )
}
